import i18n from '@/i18n'
import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Database } from '@/lib/supabase'
import { findTagByName, normalizeTagName, normalizeTagNames } from '@/lib/tagName'

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

export type AssistantPauseUnit = 'minutes' | 'hours' | 'days'

const MS_PER_MINUTE = 60_000
const MS_PER_HOUR = 60 * MS_PER_MINUTE
const MS_PER_DAY = 24 * MS_PER_HOUR
const MAX_PAUSE_MS = 30 * MS_PER_DAY

export function pauseDurationToMs(amount: number, unit: AssistantPauseUnit): number {
  if (!Number.isFinite(amount) || amount <= 0) return 0
  const ms =
    unit === 'minutes' ? amount * MS_PER_MINUTE : unit === 'hours' ? amount * MS_PER_HOUR : amount * MS_PER_DAY
  return Math.min(ms, MAX_PAUSE_MS)
}

interface UseContactManagementReturn {
  updating: boolean
  error: string | null
  assignAssistant: (contactId: string, assistantId: string | null) => Promise<boolean>
  disableAssistantTemporarily: (
    contactId: string,
    amount: number,
    unit: AssistantPauseUnit
  ) => Promise<boolean>
  disableAssistant: (contactId: string) => Promise<boolean>
  enableAssistant: (contactId: string) => Promise<boolean>
  addTag: (contactId: string, tagId: string) => Promise<boolean>
  removeTag: (contactId: string, tagId: string) => Promise<boolean>
  setTags: (contactId: string, tagIds: string[]) => Promise<boolean>
  updateChatStatus: (contactId: string, status: 'aberto' | 'concluido') => Promise<boolean>
}

export const useContactManagement = (): UseContactManagementReturn => {
  const { user } = useAuth()
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Atribui ou remove um assistente de um contato
   */
  const assignAssistant = async (contactId: string, assistantId: string | null): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setUpdating(true)
      setError(null)

      const { data: current, error: fetchError } = await supabase
        .from('account_contacts')
        .select('account_assistant_id')
        .eq('id', contactId)
        .eq('account_id', user.account_id)
        .maybeSingle()

      if (fetchError) throw fetchError

      const hadAssistant = !!current?.account_assistant_id

      const patch: Record<string, unknown> = {
        account_assistant_id: assistantId,
        assistant_id: assistantId
      }

      if (assistantId == null) {
        patch.assistant_is_active = false
        patch.assistant_disabled_until = null
      } else if (!hadAssistant) {
        patch.assistant_is_active = true
        patch.assistant_disabled_until = null
      }

      const { error: updateError } = await supabase.from('account_contacts').update(patch).eq('id', contactId).eq('account_id', user.account_id)

      if (updateError) throw updateError

      console.log('✅ [useContactManagement] Assistente atribuído:', { contactId, assistantId })
      return true
    } catch (err: any) {
      console.error('💥 [useContactManagement] Erro ao atribuir assistente:', err)
      setError(err.message || 'Erro ao atribuir assistente')
      return false
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Desativa o assistente temporariamente por minutos, horas ou dias (máx. 30 dias).
   */
  const disableAssistantTemporarily = async (
    contactId: string,
    amount: number,
    unit: AssistantPauseUnit
  ): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    const durationMs = pauseDurationToMs(amount, unit)
    if (durationMs <= 0) {
      setError(i18n.t('chat:errors.invalidPauseDuration'))
      return false
    }

    try {
      setUpdating(true)
      setError(null)

      const disabledUntil = new Date(Date.now() + durationMs)

      const { error: updateError } = await supabase
        .from('account_contacts')
        .update({
          assistant_is_active: false, // Desativar
          assistant_disabled_until: disabledUntil.toISOString() // Guardar até quando
        })
        .eq('id', contactId)
        .eq('account_id', user.account_id)

      if (updateError) throw updateError

      console.log('✅ [useContactManagement] Assistente desativado temporariamente:', { contactId, amount, unit })
      return true
    } catch (err: any) {
      console.error('💥 [useContactManagement] Erro ao desativar assistente:', err)
      setError(err.message || 'Erro ao desativar assistente')
      return false
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Desativa o assistente permanentemente
   */
  const disableAssistant = async (contactId: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setUpdating(true)
      setError(null)

      const { error: updateError } = await supabase
        .from('account_contacts')
        .update({
          assistant_is_active: false, // Desativar usando boolean
          assistant_disabled_until: null // Limpar timestamp
        })
        .eq('id', contactId)
        .eq('account_id', user.account_id)

      if (updateError) throw updateError

      console.log('✅ [useContactManagement] Assistente desativado para contato:', contactId)
      return true
    } catch (err: any) {
      console.error('💥 [useContactManagement] Erro ao desativar assistente:', err)
      setError(err.message || 'Erro ao desativar assistente')
      return false
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Reativa o assistente
   */
  const enableAssistant = async (contactId: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setUpdating(true)
      setError(null)

      const { error: updateError } = await supabase
        .from('account_contacts')
        .update({
          assistant_is_active: true, // Ativar usando boolean
          assistant_disabled_until: null // Limpar timestamp
        })
        .eq('id', contactId)
        .eq('account_id', user.account_id)

      if (updateError) throw updateError

      console.log('✅ [useContactManagement] Assistente reativado para contato:', contactId)
      return true
    } catch (err: any) {
      console.error('💥 [useContactManagement] Erro ao reativar assistente:', err)
      setError(err.message || 'Erro ao reativar assistente')
      return false
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Adiciona uma tag ao contato e cria na tabela account_tags se não existir
   */
  const addTag = async (contactId: string, tagName: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setUpdating(true)
      setError(null)

      const normalizedTagName = normalizeTagName(tagName)
      if (!normalizedTagName) return false

      // A comparação é por nome normalizado, não por `eq`: uma linha antiga
      // gravada como "VIP" não casaria com "vip" e ganharia uma tag gêmea.
      const { data: accountTags } = await supabase
        .from('account_tags')
        .select('id, name')
        .eq('account_id', user.account_id)

      const existingTag = findTagByName(accountTags || [], normalizedTagName)

      if (!existingTag) {
        // Criar a tag em account_tags
        await supabase
          .from('account_tags')
          .insert({
            account_id: user.account_id,
            name: normalizedTagName,
            color: '#3B82F6',
            is_active: true
          })
      }

      // Buscar tags atuais do contato
      const { data: contact, error: fetchError } = await supabase
        .from('account_contacts')
        .select('tags')
        .eq('id', contactId)
        .eq('account_id', user.account_id)
        .maybeSingle()

      if (fetchError) throw fetchError
      if (!contact) throw new Error(i18n.t('crm:errors.contactNotFound'))

      const currentTags = contact.tags || []
      if (currentTags.some((tag: string) => normalizeTagName(tag) === normalizedTagName)) {
        return true // Tag já existe no contato
      }

      const newTags = [...currentTags, normalizedTagName]

      const { error: updateError } = await supabase
        .from('account_contacts')
        .update({ tags: newTags })
        .eq('id', contactId)
        .eq('account_id', user.account_id)

      if (updateError) throw updateError

      return true
    } catch (err: any) {
      console.error('💥 [useContactManagement] Erro ao adicionar tag:', err)
      setError(err.message || 'Erro ao adicionar tag')
      return false
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Remove uma tag do contato
   */
  const removeTag = async (contactId: string, tagName: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setUpdating(true)
      setError(null)

      const normalizedTagName = tagName.trim().toLowerCase()

      // Buscar tags atuais
      const { data: contact, error: fetchError } = await supabase
        .from('account_contacts')
        .select('tags')
        .eq('id', contactId)
        .eq('account_id', user.account_id)
        .maybeSingle()

      if (fetchError) throw fetchError
      if (!contact) throw new Error(i18n.t('crm:errors.contactNotFound'))

      const currentTags = contact.tags || []
      const newTags = currentTags.filter((name: string) => name !== normalizedTagName)

      const { error: updateError } = await supabase
        .from('account_contacts')
        .update({ tags: newTags })
        .eq('id', contactId)
        .eq('account_id', user.account_id)

      if (updateError) throw updateError

      return true
    } catch (err: any) {
      console.error('💥 [useContactManagement] Erro ao remover tag:', err)
      setError(err.message || 'Erro ao remover tag')
      return false
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Define todas as tags do contato de uma vez
   */
  const setTags = async (contactId: string, tagNames: string[]): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setUpdating(true)
      setError(null)

      const normalizedTags = normalizeTagNames(tagNames)

      const { error: updateError } = await supabase
        .from('account_contacts')
        .update({ tags: normalizedTags })
        .eq('id', contactId)
        .eq('account_id', user.account_id)

      if (updateError) throw updateError

      return true
    } catch (err: any) {
      console.error('💥 [useContactManagement] Erro ao definir tags:', err)
      setError(err.message || 'Erro ao definir tags')
      return false
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Atualiza o status do chat do contato
   */
  const updateChatStatus = async (contactId: string, status: 'aberto' | 'concluido'): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setUpdating(true)
      setError(null)

      const { error: updateError } = await supabase
        .from('account_contacts')
        .update({ status_chat: status })
        .eq('id', contactId)
        .eq('account_id', user.account_id)

      if (updateError) throw updateError

      console.log('✅ [useContactManagement] Status do chat atualizado:', { contactId, status })
      return true
    } catch (err: any) {
      console.error('💥 [useContactManagement] Erro ao atualizar status do chat:', err)
      setError(err.message || 'Erro ao atualizar status do chat')
      return false
    } finally {
      setUpdating(false)
    }
  }

  return {
    updating,
    error,
    assignAssistant,
    disableAssistantTemporarily,
    disableAssistant,
    enableAssistant,
    addTag,
    removeTag,
    setTags,
    updateChatStatus,
  }
}
